import {
  ClientGroupMembershipStatusEnum,
  type ClientPersonReference
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { useCallback, useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, View } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';

import { useGetGroup } from '@/features/api/hooks/grapevine-group/useGetGroup';
import { useGetGroupAdmins } from '@/features/api/hooks/grapevine-group/useGetGroupAdmins';
import { useGetInfiniteGroupMembers } from '@/features/api/hooks/grapevine-group/useGetInfiniteGroupMembers';
import { useAppState } from '@/features/authorization/provider/useAppState';
import { GroupMemberItem } from '@/features/groups/components/members/GroupMemberItem';
import { ListScreenLayout } from '@/features/groups/components/members/ListScreenLayout';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import type {
  AppNativeStackScreenProps,
  AppRoute
} from '@/navigation/types/navigation';
import { Refresher } from '@/shared/components/state-view/Refresher';
import { useRefetchOnFocus } from '@/shared/hooks/useRefetchOnFocus';
import { webScrollBarStyle } from '@/styles/customStyles';

export type Props = AppNativeStackScreenProps<typeof AppRoute.GroupMembers>;

export const GroupMembersScreen: FC<Props> = ({ route }) => {
  const { groupId } = route.params;

  const { t } = useTranslation(LocalizationNameSpace.GROUPS, {
    keyPrefix: 'screens.members'
  });

  const { userId: ownUserId } = useAppState();
  const groupQuery = useGetGroup(groupId);
  const {
    data: group,
    isRefetching: isGroupRefetching,
    refetch: refetchGroup
  } = groupQuery;
  const membersQuery = useGetInfiniteGroupMembers(groupId);
  const {
    data: members,
    isRefetching: isMembersRefetching,
    refetch: refetchMembers,
    isFetchingNextPage: isFetchingNextMembersPage,
    fetchNextPage: fetchNextMembersPage
  } = membersQuery;
  const adminsQuery = useGetGroupAdmins(groupId);
  const {
    data: admins,
    isRefetching: isAdminsRefetching,
    refetch: refetchAdmins
  } = adminsQuery;

  const isMember =
    group?.membershipStatus === ClientGroupMembershipStatusEnum.Approved;

  // Todo: should we use "useRefetchOnFocus" everywhere?
  useRefetchOnFocus(groupQuery);
  useRefetchOnFocus(membersQuery);
  useRefetchOnFocus(adminsQuery);

  const adminIds = useMemo(
    () => new Set<string>(admins?.map((admin) => admin.id)),
    [admins]
  );
  const currentUserIsAdmin = adminIds.has(ownUserId);

  const isRefetching =
    isGroupRefetching || isMembersRefetching || isAdminsRefetching;

  const handleRefresh = useCallback(() => {
    refetchGroup();
    refetchMembers();
    refetchAdmins();
  }, [refetchAdmins, refetchGroup, refetchMembers]);

  return (
    <ListScreenLayout
      title={t('members', { count: group?.memberCount })}
      list={
        <FlatList<ClientPersonReference>
          data={members}
          extraData={adminIds}
          refreshing={isRefetching}
          onRefresh={handleRefresh}
          refreshControl={<Refresher refreshing={isRefetching} />}
          onEndReachedThreshold={0.8}
          onEndReached={() => fetchNextMembersPage()}
          ListFooterComponent={
            <View>{isFetchingNextMembersPage && <ActivityIndicator />}</View>
          }
          renderItem={({ item: member }) => (
            <GroupMemberItem
              groupId={groupId}
              member={member}
              memberIsAdmin={adminIds.has(member.id)}
              currentUserIsMember={isMember}
              currentUserIsAdmin={currentUserIsAdmin}
            />
          )}
          keyExtractor={(member, idx) => member.id ?? idx.toString()}
          style={webScrollBarStyle}
        />
      }
    />
  );
};
